import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import withDirection, { DIRECTIONS, withDirectionPropTypes } from 'react-with-direction';

import { APP_TEXT_COLOR_PATH } from '../../constants/wix-params';
import withCardBackgroundColor from '../../hoc/with-card-background-color';
import withFontClassName from '../../hoc/with-font-class-name';
import withTranslate from '../../hoc/with-translate';
import styles from './more-button.scss';
import MoreIcon from '../icons/more-icon';
import { handleEnterKeyUp, handleEscapeKeyUp } from '../../services/accessibility';
import withSettingsColor from '../../hoc/with-settings-color';

const BOTTOM_THRESHOLD = 45;
const POSITION_REVERSE_MAP = {
  left: 'right',
  right: 'left',
};
const SIZE_SMALL = 'small';

export class MoreButton extends Component {
  state = {
    isVisible: false,
  };

  handleClick = event => {
    if (this.state.isVisible) {
      this.hideComponent();
    } else {
      this.showComponent();
    }
    event.preventDefault();
  };

  setContainer = element => (this.container = element);

  setActionsContainer = element => (this.actionsContainer = element);

  checkIfFits = () => {
    const rect = this.container.getBoundingClientRect();
    const clientHeight = document.documentElement.clientHeight;
    const actionsHeight = this.actionsContainer ? this.actionsContainer.offsetHeight : 0;
    const fitDown = clientHeight - rect.top > actionsHeight + BOTTOM_THRESHOLD;
    const fitUp = !fitDown && actionsHeight !== 0 && rect.top > actionsHeight;
    this.setState({
      isAtTop: fitUp,
    });
  };

  showComponent = () => {
    document.addEventListener('click', this.hideComponent);
    this.setState({ isVisible: true }, this.checkIfFits);
    this.props.onShow();
  };

  hideComponent = () => {
    document.removeEventListener('click', this.hideComponent);
    this.setState({ isVisible: false });
    this.props.onHide();
  };

  componentWillUnmount = () => {
    document.removeEventListener('click', this.hideComponent);
  };

  renderActionsContainer = () => {
    const { contentFontClassName, cardBackgroundColor, direction, position } = this.props;
    const actionsContainerClass = classNames(
      styles.actions,
      contentFontClassName,
      'default-desktop-header-text-color',
      'default-forum-card-background-color',
      { [styles.actionsTop]: this.state.isAtTop },
    );
    const effectivePosition =
      direction === DIRECTIONS.LTR ? position : POSITION_REVERSE_MAP[position];
    const actionsContainerStyle = {
      backgroundColor: cardBackgroundColor,
      ...(effectivePosition === 'left' ? { right: 0 } : { left: 0 }),
    };

    return (
      <div
        className={actionsContainerClass}
        style={actionsContainerStyle}
        role="menu"
        aria-live="polite"
        ref={this.setActionsContainer}
        data-hook="actions"
      >
        {this.props.children}
      </div>
    );
  };

  render = () => {
    const { id, icon, t, isWhite, className: buttonClassName, iconClassName, size } = this.props;
    const className = classNames(buttonClassName, styles.more, styles[size], 'more-button', {
      [styles.moreWhite]: isWhite,
    });

    return (
      <div
        onClick={this.handleClick}
        className={className}
        ref={this.setContainer}
        onKeyUp={handleEscapeKeyUp(this.hideComponent)}
        data-hook="more-button"
      >
        <div
          className={classNames(styles.icon, 'button-hover-fill', iconClassName)}
          tabIndex="0"
          role="menubar"
          aria-label={t('more-button.more-actions')}
          onKeyUp={handleEnterKeyUp(this.handleClick)}
          id={id}
        >
          {icon}
        </div>
        {this.state.isVisible ? this.renderActionsContainer() : null}
      </div>
    );
  };
}

MoreButton.propTypes = {
  cardBackgroundColor: PropTypes.string.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  cardBorderColor: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  iconClassName: PropTypes.string,
  isWhite: PropTypes.bool,
  id: PropTypes.string,
  icon: PropTypes.node,
  t: PropTypes.func.isRequired,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  ...withDirectionPropTypes,
  position: PropTypes.oneOf(['left', 'right']),
  size: PropTypes.oneOf([SIZE_SMALL]),
};

MoreButton.defaultProps = {
  icon: <MoreIcon className={classNames(styles.moreIcon)} />,
  iconClassName: 'forum-icon-fill',
  position: 'left',
  onShow: () => {},
  onHide: () => {},
};

MoreButton.SIZE_SMALL = SIZE_SMALL;

export default flowRight(
  withDirection,
  withSettingsColor({
    path: APP_TEXT_COLOR_PATH,
    propName: 'cardBorderColor',
    alpha: 0.25,
    siteColorFallback: 'color-5',
    siteColorAlpha: 0.25,
    fallbackColor: '#000000',
  }),
  withCardBackgroundColor,
  withTranslate,
  withFontClassName,
)(MoreButton);
